import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatCents, type AiCostSummary } from '@/lib/api/ai-costs';

/**
 * Work-class spend donut (T-0112 / C20-122).
 *
 * Per-work-class share of MTD spend rendered as a Recharts donut. The
 * fixed palette uses ink + amber + posture tones so each slice picks a
 * brand-aligned colour — never a raw hex outside the token list.
 */
export interface WorkClassDonutProps {
  summary: AiCostSummary;
}

// Pulled from the design tokens — see globals.css / tokens.css. Order
// matches the work-class id order so each slice is stable across renders.
const SLICE_COLORS: Record<string, string> = {
  'base-form': 'var(--ink-700)',
  ongoing: 'var(--accent-amber-600)',
  reeval: 'var(--accent-amber-500)',
  chat: 'var(--posture-healthy)',
  narrative: 'var(--posture-at-risk)',
  embed: 'var(--ink-300)',
};

interface DonutDatum {
  id: string;
  name: string;
  value: number; // cents
}

export function WorkClassDonut({ summary }: WorkClassDonutProps) {
  const total = summary.per_work_class.reduce((acc, w) => acc + w.total_cents, 0);
  const data: DonutDatum[] = summary.per_work_class.map((w) => ({
    id: w.id,
    name: w.name,
    value: w.total_cents,
  }));

  return (
    <div className="space-y-2">
      <div
        role="img"
        aria-label="Work-class spend breakdown"
        style={{ width: '100%', height: 220 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={50}
              outerRadius={86}
              paddingAngle={2}
              stroke="var(--paper-0)"
              strokeWidth={2}
              isAnimationActive
            >
              {data.map((d) => (
                <Cell key={d.id} fill={SLICE_COLORS[d.id] ?? 'var(--ink-500)'} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => [formatCents(value), '']}
              contentStyle={{
                background: 'var(--paper-0)',
                border: '1px solid var(--ink-100)',
                borderRadius: 8,
                fontSize: 12,
                color: 'var(--ink-900)',
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-2 text-meta text-ink-700">
        {data.map((d) => {
          const pct = total > 0 ? (d.value / total) * 100 : 0;
          return (
            <div key={d.id} className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: SLICE_COLORS[d.id] }}
              />
              <span>{d.name}</span>
              <span className="font-mono text-ink-500">{pct.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
